import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type BackendStatus = 'connected' | 'disconnected' | 'checking';

const STATUS_CACHE_KEY = 'clando-backend-status';

export function useBackendStatus() {
  // Initialize optimistically: if online, assume connected unless last known state was disconnected
  const [status, setStatus] = useState<BackendStatus>(() => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return 'disconnected';
    
    try {
      const cached = localStorage.getItem(STATUS_CACHE_KEY);
      if (cached === 'disconnected') return 'checking'; // Indication we might be offline
      return 'connected'; // Optimistic default
    } catch {
      return 'connected';
    }
  });

  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const checkCountRef = useRef(0);
  const checkingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const check = useCallback(async (isManual = false) => {
    checkCountRef.current++;
    const currentCheckId = checkCountRef.current;

    if (!navigator.onLine) {
      setStatus('disconnected');
      setLastChecked(new Date());
      localStorage.setItem(STATUS_CACHE_KEY, 'disconnected');
      return;
    }

    // If we are currently "connected" and this isn't a manual recheck, 
    // don't jump to "checking" immediately to avoid UI flicker.
    // We only show "checking" if the request takes more than 1.5s.
    if (status === 'connected' || status === 'disconnected') {
      if (checkingTimeoutRef.current) clearTimeout(checkingTimeoutRef.current);
      
      checkingTimeoutRef.current = setTimeout(() => {
        if (checkCountRef.current === currentCheckId) {
          setStatus('checking');
        }
      }, 1500);
    } else if (isManual) {
        setStatus('checking');
    }

    try {
      // Lightweight query to check connectivity
      const { error } = await supabase
        .from('app_configuration')
        .select('key')
        .limit(1)
        .maybeSingle();

      const nextStatus = error ? 'disconnected' : 'connected';
      
      if (checkingTimeoutRef.current) {
        clearTimeout(checkingTimeoutRef.current);
        checkingTimeoutRef.current = undefined;
      }

      setStatus(nextStatus);
      localStorage.setItem(STATUS_CACHE_KEY, nextStatus);
    } catch {
      if (checkingTimeoutRef.current) {
        clearTimeout(checkingTimeoutRef.current);
        checkingTimeoutRef.current = undefined;
      }
      setStatus('disconnected');
      localStorage.setItem(STATUS_CACHE_KEY, 'disconnected');
    }
    setLastChecked(new Date());
  }, [status]);

  useEffect(() => {
    // Initial check
    check();

    // Check every 5 minutes (was 30s — too aggressive, hits DB per tab)
    const startInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => check(), 5 * 60 * 1000);
    };
    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };

    // Only run the heartbeat while the tab is visible.
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      startInterval();
    }
    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        check(); // immediate refresh on resume
        startInterval();
      } else {
        stopInterval();
      }
    };

    const handleOnline = () => check();
    const handleOffline = () => {
      setStatus('disconnected');
      setLastChecked(new Date());
      localStorage.setItem(STATUS_CACHE_KEY, 'disconnected');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      stopInterval();
      if (checkingTimeoutRef.current) clearTimeout(checkingTimeoutRef.current);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [check]);

  return { status, lastChecked, recheck: () => check(true) };
}

